import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, PackageCheck, Plus, Tag } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { EmptyState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { confirmModal, Modal } from '@/components/ui/modal'
import { FormSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { isApiError } from '@/lib/api/errors'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../shared/format'
import { useDeleteRelease, useSaveRelease } from '../api'
import { PROJECT_RELEASE_PERM } from '../permissions'
import { RELEASE_CHANNELS, type Project, type Release } from '../types'
import { toastError } from '../ui'
import { RowMenu } from './RowMenu'

export function ReleasesPanel({ project }: { project: Project }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const remove = useDeleteRelease(project.id)
  const [dialog, setDialog] = useState<{ open: boolean; release: Release | null }>({ open: false, release: null })
  const canWrite = can(PROJECT_RELEASE_PERM)
  const releases = [...project.releases].sort((a, b) => b.releasedAt.localeCompare(a.releasedAt))

  const onDelete = async (r: Release) => {
    const ok = await confirmModal({
      title: t('projects.releases.deleteTitle'),
      description: t('projects.releases.deleteBody', { version: r.version, project: project.name }),
      confirmLabel: t('common.delete'),
      destructive: true,
    })
    if (!ok) return
    try {
      await remove.mutateAsync(r.id)
      toast.success(t('projects.releases.deleted'))
    } catch (e) {
      toastError(e)
    }
  }

  return (
    <section className="space-y-4 p-4 sm:p-5">
      {canWrite ? (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setDialog({ open: true, release: null })}>
            <Plus />
            {t('projects.releases.add')}
          </Button>
        </div>
      ) : null}
      {releases.length === 0 ? (
        <div className="rounded-xl border">
          <EmptyState icon={PackageCheck} title={t('projects.releases.empty')} />
        </div>
      ) : (
        <ol className="space-y-3">
          {releases.map((r) => (
            <li key={r.id} className="rounded-xl border bg-card p-3 shadow-card transition-shadow hover:shadow-card-hover sm:p-4">
              <div className="flex items-start gap-3">
                <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', r.channel === 'stable' ? 'bg-success/12 text-success' : 'bg-warning/15 text-warning-strong')}>
                  <Tag className="size-4" aria-hidden />
                </span>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{r.version}</span>
                    <Badge tone={r.channel === 'stable' ? 'success' : 'warning'}>{t(`projects.releases.channel.${r.channel}`)}</Badge>
                    <span className="text-xs tabular-nums text-muted-foreground">{fmtDate(r.releasedAt)}</span>
                  </div>
                  {r.notes ? <p className="text-sm text-muted-foreground">{r.notes}</p> : null}
                </div>
                {canWrite ? <RowMenu label={r.version} onEdit={() => setDialog({ open: true, release: r })} onDelete={() => void onDelete(r)} /> : null}
              </div>
            </li>
          ))}
        </ol>
      )}
      {dialog.open ? <ReleaseForm key={dialog.release?.id ?? 'new'} project={project} release={dialog.release} onDone={() => setDialog({ open: false, release: null })} /> : null}
    </section>
  )
}

function ReleaseForm({ project, release, onDone }: { project: Project; release: Release | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveRelease(project.id)

  const schema = z.object({
    version: z.string().trim().regex(/^v?\d+(\.\d+){0,2}(-[0-9A-Za-z.]+)?$/, t('projects.releases.form.versionInvalid')),
    channel: z.enum(RELEASE_CHANNELS),
    releasedAt: z.string().min(1, t('projects.form.dateRequired')),
    notes: z.string().trim().max(400),
  })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { version: release?.version ?? '', channel: release?.channel ?? 'stable', releasedAt: release?.releasedAt ?? '', notes: release?.notes ?? '' },
  })
  const { errors } = form.formState

  const onSubmit = form.handleSubmit(async (input) => {
    try {
      await save.mutateAsync({ id: release?.id, input })
      toast.success(t(release ? 'projects.releases.updated' : 'projects.releases.created'))
      onDone()
    } catch (e) {
      if (isApiError(e) && e.code === 'RELEASE_VERSION_TAKEN') form.setError('version', { message: t('errors.RELEASE_VERSION_TAKEN') })
      else toastError(e)
    }
  })

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<PackageCheck />}
      title={t(release ? 'projects.releases.form.editTitle' : 'projects.releases.form.createTitle')}
      description={t('projects.releases.form.hint', { project: project.name })}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="rel-version">{t('projects.releases.form.version')}</Label>
            <Input id="rel-version" placeholder="v2.7" aria-invalid={!!errors.version} {...form.register('version')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rel-channel">{t('projects.releases.form.channel')}</Label>
            <FormSelect id="rel-channel" control={form.control} name="channel" options={RELEASE_CHANNELS.map((c) => ({ value: c, label: t(`projects.releases.channel.${c}`) }))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rel-date">{t('projects.releases.form.date')}</Label>
            <Controller control={form.control} name="releasedAt" render={({ field }) => <DateField id="rel-date" value={field.value} onChange={field.onChange} aria-invalid={!!errors.releasedAt} />} />
          </div>
        </div>
        {errors.version || errors.releasedAt ? <p className="text-xs text-destructive">{errors.version?.message ?? errors.releasedAt?.message}</p> : null}
        <div className="space-y-2">
          <Label htmlFor="rel-notes">{t('projects.releases.form.notes')}</Label>
          <Textarea id="rel-notes" rows={3} maxLength={400} {...form.register('notes')} />
        </div>
      </div>
    </Modal>
  )
}
